import { ServerDevice } from '@/Devices/types';

export type ChipStatusType = 'default' | 'leak' | 'sensor' | 'connection';

// The backend reports a freshly installed, healthy device as ERROR_SENSOR with this
// message until its first statistics arrive. It is not an error for the customer, so
// every rollup and chip must treat it as OKAY.
export const INSTALLATION_CHECK_IN_PROGRESS = 'Installation Check In Progress';

export const isInstallationCheckInProgress = (deviceStatusMessage?: string): boolean =>
  deviceStatusMessage === INSTALLATION_CHECK_IN_PROGRESS;

interface ChipStatusProps {
  bgColor: string;
  color: string;
  type: ChipStatusType;
}

// order of severity for which device status is displayed in side drawer
export const STATUS_SEVERITY_RANK: Record<string, number> = {
  ERROR_CONNECTION: 1,
  ERROR_SENSOR: 2,
  ERROR_LEAK: 3,
  OKAY: 4,
  NO_DEVICES: 5,
};

const getSeverity = (device: ServerDevice): number => {
  if (isInstallationCheckInProgress(device.device_status_strings?.[0])) {
    return STATUS_SEVERITY_RANK.OKAY;
  }
  return STATUS_SEVERITY_RANK[device.device_status] ?? 99;
};

// function to get the worst device based on status severity
export function getWorstDevice(devices: ServerDevice[]): ServerDevice | null {
  if (!devices || devices.length === 0) return null;

  return [...devices].sort((a, b) => getSeverity(a) - getSeverity(b))[0];
}

export function getChipStatusProps(
  deviceStatus: string,
  deviceStatusMessage?: string,
): ChipStatusProps | null {
  // Override based on status message
  if (isInstallationCheckInProgress(deviceStatusMessage)) {
    return {
      bgColor: '#E8F3EE',
      color: '#198754',
      type: 'default',
    };
  }

  switch (deviceStatus) {
    case 'ERROR_CONNECTION':
      return {
        bgColor: '#FCEBEC',
        color: '#DC3545',
        type: 'connection',
      };
    case 'ERROR_SENSOR':
      return {
        bgColor: '#FCEBEC',
        color: '#DC3545',
        type: 'sensor',
      };
    case 'ERROR_LEAK':
      return {
        bgColor: '#FFF9E6',
        color: '#6B5103',
        type: 'leak',
      };
    case 'OKAY':
      return {
        bgColor: '#E8F3EE',
        color: '#198754',
        type: 'default',
      };
    default:
      return null;
  }
}
